import type { Editor } from '@tiptap/react';
import isNil from 'lodash/isNil';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { Button, ButtonVariant } from '@ui/components/base/button';
import { Div } from '@ui/components/base/div';
import { CustomIconButton, type CustomIconButtonProps } from '@ui/components/buttons/customIconButton';
import { type TemplateContentColor, toComparableColor } from '@ui/components/editor/templateTextUtils';
import { useEditorFocusReturn } from '@ui/components/editor/useEditorFocusReturn';
import { Popover } from '@ui/components/popovers/Popover';

const COLOR_MESSAGE_PREFIX = 'richTextEditor.colors';

interface RichTextColorPopoverProps {
  editor: Editor;
  label: string;
  resetLabel: string;
  icon: CustomIconButtonProps['icon'];
  colors: TemplateContentColor[];
  value: string | null;
  onApply: (color: string) => void;
  onReset: () => void;
}

export const RichTextColorPopover = ({
  editor,
  label,
  resetLabel,
  icon,
  colors,
  value,
  onApply,
  onReset,
}: RichTextColorPopoverProps) => {
  const { formatMessage } = useIntl();
  const [open, setOpen] = useState<boolean>(false);
  const { returnFocusToEditorOnClose, finalFocus } = useEditorFocusReturn(editor);
  const currentColor = isNil(value) ? null : toComparableColor(value);

  const closeOnEditor = () => {
    returnFocusToEditorOnClose();
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      align="start"
      className="w-auto gap-2 p-2"
      finalFocus={finalFocus}
      trigger={
        <CustomIconButton
          type="button"
          icon={icon}
          variant={isNil(value) ? ButtonVariant.GHOST : ButtonVariant.SECONDARY}
          aria-label={label}
          tooltipText={label}
        />
      }
    >
      <Div grid role="group" aria-label={label} className="grid-cols-5 gap-1">
        {colors.map((color) => {
          const selected = currentColor === toComparableColor(color.value);
          return (
            <Button
              key={color.value}
              type="button"
              variant={ButtonVariant.GHOST}
              size="icon-sm"
              className="aria-pressed:ring-2 aria-pressed:ring-primary"
              aria-label={formatMessage({ id: `${COLOR_MESSAGE_PREFIX}.${color.name}` })}
              aria-pressed={selected}
              onClick={() => {
                onApply(color.value);
                closeOnEditor();
              }}
            >
              <span
                aria-hidden
                className="size-5 rounded-sm border border-border"
                style={{ backgroundColor: color.value }}
              />
            </Button>
          );
        })}
      </Div>
      <Button
        type="button"
        variant={ButtonVariant.GHOST}
        size="sm"
        onClick={() => {
          onReset();
          closeOnEditor();
        }}
      >
        {resetLabel}
      </Button>
    </Popover>
  );
};
